import type { Ref } from 'vue'
import type { DateRequestFlow } from '~/composables/useDateRequestFlow'
import type { DateRequestFormModel } from '~/composables/useDateRequestFormState'

interface InstantSyncOptions {
  clearErrors?: (field?: string) => void
}

interface UserProfile {
  first_name?: string
  last_name?: string
  email?: string
}

/**
 * Keeps the form in sync with the instant-booking variant:
 * - applies the signed-in user's profile when we switch into instant mode
 *   (or when the user signs in while already in instant mode),
 * - trims the dates array down to a single slot entering instant mode,
 * - pads it back to three slots when leaving instant mode,
 * - ensures a `dateType` default while in instant mode.
 *
 * Consolidates three previously overlapping watchers into one watcher graph.
 */
export const useDateRequestInstantSync = (
  form: Ref<DateRequestFormModel>,
  flow: DateRequestFlow,
  options: InstantSyncOptions = {}
) => {
  const eventData = useEventData()
  const { user } = useAccount()

  const applyProfileToInstantForm = () => {
    const profile = (user.value as unknown as { data?: UserProfile } | null)
      ?.data
    if (!profile) {
      return
    }
    if (profile.first_name) {
      form.value.name = profile.first_name
    }
    if (profile.last_name) {
      form.value.surname = profile.last_name
    }
    if (profile.email) {
      form.value.email = profile.email
    }
  }

  const ensureInstantDateRequestDefaults = () => {
    const ev = eventData.value
    if (!ev) {
      return
    }
    if (form.value.dateType !== 'PUBLIC' && form.value.dateType !== 'PRIVATE') {
      form.value.dateType = eventGetters.isInstantBookingPrivate(ev)
        ? 'PRIVATE'
        : 'PUBLIC'
    }
  }

  const trimDatesToSingleInstantSlot = () => {
    const first =
      form.value.dates.find((d) => d && String(d).trim() !== '') ??
      form.value.dates[0] ??
      ''
    form.value.dates = first ? [String(first)] : ['']
  }

  const padDatesToThreeSlots = () => {
    const d = [...form.value.dates]
    while (d.length < 3) {
      d.push('')
    }
    form.value.dates = d.slice(0, 3)
  }

  watch(
    [flow.isInstantDateRequestSimplifiedUi, user],
    ([active, _user], prev) => {
      const prevActive = Array.isArray(prev) ? prev[0] : undefined
      if (active) {
        applyProfileToInstantForm()
        ensureInstantDateRequestDefaults()
        // Only clear errors + trim slots on mode transition, not every user change.
        if (prevActive !== true) {
          options.clearErrors?.()
          trimDatesToSingleInstantSlot()
        }
      } else if (prevActive === true) {
        padDatesToThreeSlots()
      }
    },
    { immediate: true }
  )

  return {
    applyProfileToInstantForm,
    ensureInstantDateRequestDefaults,
    trimDatesToSingleInstantSlot,
    padDatesToThreeSlots,
  }
}
